/**
 * TanStack Query hooks for the staging dev tools.
 *
 * The capabilities query doubles as the feature detector. The backend does not
 * mount these routes when APP_ENV=production, so `/capabilities` 404s there —
 * and a 404 is a definitive "this environment has no dev tools", not a
 * transient failure. Asking the server beats reading a `VITE_*` build var: the
 * server is the only thing that knows the truth, and a stale frontend build
 * must not be able to offer a surface the backend doesn't have.
 *
 * NOTE this deliberately does NOT follow `useExperimentDevPanelEnabled`, which
 * gates on `?exp-debug=1` + localStorage + `import.meta.env.DEV`. That panel is
 * a pure client overlay — the worst a wrong answer does is show a developer
 * some JSON. These endpoints create orders, transmit to pharmacies and write to
 * the live Healthie org, so the gate belongs on the server where it cannot be
 * flipped from a console. The cost is one 404 per app load in production; that
 * is the correct trade.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError, apiFetch } from './client';
import type {
  AdvanceOrderRequest,
  AdvanceOrderResponse,
  CreateTestOrderRequest,
  CreateTestOrderResponse,
  DevToolsCapabilities,
  DevToolsProductsResponse,
  WritePrescriptionResponse,
} from '@/types/devTools';

const BASE = '/admin/dev-tools';

const capabilitiesKey = ['admin', 'dev-tools', 'capabilities'];

export function useDevToolsCapabilities() {
  return useQuery({
    queryKey: capabilitiesKey,
    queryFn: () => apiFetch<DevToolsCapabilities>(`${BASE}/capabilities`),
    // Whether an environment has dev tools cannot change without a redeploy,
    // so this never goes stale within a session.
    staleTime: Infinity,
    // A 404 is the answer ("no dev tools here"), not a failure worth retrying
    // — and on production that is every single call. 401/403 likewise say
    // something definitive about the caller rather than the network.
    retry: (failureCount, error) => {
      if (error instanceof ApiError && error.status < 500) return false;
      return failureCount < 2;
    },
  });
}

/** True only when the backend actually served capabilities. */
export function useDevToolsAvailable(): boolean {
  const { data } = useDevToolsCapabilities();
  return Boolean(data?.enabled);
}

export function useDevToolsProducts(enabled = true) {
  return useQuery({
    queryKey: ['admin', 'dev-tools', 'products'],
    queryFn: () => apiFetch<DevToolsProductsResponse>(`${BASE}/products`),
    enabled,
    staleTime: 300_000,
  });
}

/** Invalidate everything a mutation could have moved: this patient's orders
 *  tab, their prescriptions, and the summary card's counts. */
function useInvalidatePatient(patientId: string) {
  const qc = useQueryClient();
  return () => {
    for (const tab of ['orders', 'prescriptions', 'summary', 'lifecycle']) {
      void qc.invalidateQueries({
        queryKey: ['admin', 'clinical-admin', 'patient', patientId, tab],
      });
    }
  };
}

export function useWriteTestPrescription(patientId: string) {
  const invalidate = useInvalidatePatient(patientId);
  return useMutation({
    mutationFn: (productId: string) =>
      apiFetch<WritePrescriptionResponse>(
        `${BASE}/patients/${patientId}/prescriptions`,
        { method: 'POST', body: JSON.stringify({ product_id: productId }) },
      ),
    onSuccess: invalidate,
  });
}

export function useCreateTestOrder(patientId: string) {
  const invalidate = useInvalidatePatient(patientId);
  return useMutation({
    mutationFn: (body: CreateTestOrderRequest) =>
      apiFetch<CreateTestOrderResponse>(`${BASE}/patients/${patientId}/orders`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: invalidate,
  });
}

export function useAdvanceOrder(patientId: string) {
  const invalidate = useInvalidatePatient(patientId);
  return useMutation({
    mutationFn: ({
      orderId,
      ...body
    }: AdvanceOrderRequest & { orderId: string }) =>
      apiFetch<AdvanceOrderResponse>(`${BASE}/orders/${orderId}/advance`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: invalidate,
  });
}
